// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'components';
import CheckBox from 'components/CheckBox';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLetter } from 'utils';
import { MgmtFeeTypeEnum } from 'views/shared/types';
import { PaymentCardListSkeleton } from 'components/Skeleton';
import { useMutation, useQuery } from '@apollo/client';
import { NavigationPath } from 'types/DomainTypes';
import Alert from 'components/Alert';
import { CardDetailResponse } from '__generated__/graphql';
import { AccountViewType, OpenSlideoutFnType, SlideOutPanelViews, SubjectOptionKeys } from '../types';
import AddCardButton from './AddCardButton';
import Card from './Checkout.Com/Card';
import EditCheckoutDotComCard from './slideoutTemplates/ManageCheckoutDotComCard';
import { useGetCheckoutDotComCards } from '../hooks/useGetCheckoutDotComCards';
import CardDetailsForm, { FlowType } from './Checkout.Com/CardDetailsForm';
import { cardHasExpired, selectCheckoutDotComCards } from '../utils';
import { ReactComponent as WarningIcon } from '../../../assets/icons/warning-black.svg';
import { ACCEPTED_PAYMENT_METHODS } from '../constants';
import CardMitModel from './CardMitModel';
import { GET_USER_PREFERENCES } from '../graphql/getUserPreferences';
import { PortalClientPreferencesType } from './ManagementFees/types';
import { UPDATE_USER_PREFERENCES } from '../graphql/updateUserPreferences';

interface PaymentsProps {
  openSlideout: OpenSlideoutFnType;
  onClose: () => void;
}

enum ToggleMitModelEnum {
  NONE = 'none',
  ENABLE = 'enable',
  DISABLE = 'disable',
  ERROR = 'error',
}

const feeType = MgmtFeeTypeEnum.MONTHLY;

export default function Payments({ openSlideout, onClose }: PaymentsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, error, loading } = useGetCheckoutDotComCards();
  const _cards = selectCheckoutDotComCards(data);
  const [isLoadingUpdatePreference, setIsLoadingUpdatePreference] = useState(false);
  const [toggleMitConfig, setToggleMitConfig] = useState({
    isOpen: false,
    mitEnabled: false,
    selectedId: ToggleMitModelEnum.NONE,
    hidden: false,
  });
  const {
    data: userPreferences,
    loading: loadingUserPreferences,
    refetch: refetchUserPreferences,
  } = useQuery(GET_USER_PREFERENCES, {
    fetchPolicy: 'network-only',
  });

  const [updatePreferenceMutationApi] = useMutation(UPDATE_USER_PREFERENCES);

  useEffect(() => {
    if (loadingUserPreferences) return;
    const userSetttings = (userPreferences as PortalClientPreferencesType).portalClientPreferences;
    if (String(userSetttings.managementFeePayment).toLowerCase() === feeType) {
      setToggleMitConfig((prev) => ({
        ...prev,
        mitEnabled: true,
      }));
    }

    if (String(userSetttings.managementFeePayment).toLowerCase() === MgmtFeeTypeEnum.YEARLY) {
      setToggleMitConfig((prev) => ({
        ...prev,
        hidden: true,
      }));
    }
  }, [userPreferences, loadingUserPreferences]);

  const mitCheckBoxDisabled = useMemo(() => {
    const validCardInfo = _cards
      .slice()
      .sort((a) => (a.defaultMitFlag ? -1 : 1))
      .map((card) => {
        const valid = ACCEPTED_PAYMENT_METHODS.find(
          (x) => x.toLocaleLowerCase() === (card?.scheme || '').toLocaleLowerCase(),
        );
        if (valid) {
          return card;
        }

        return null;
      })
      .filter((x) => x !== null && x?.mitConsentedFlag && x?.defaultMitFlag);

    return validCardInfo.length === 0;
  }, [_cards]);

  const handleUpdateManagentFeePayment = async () => {
    setIsLoadingUpdatePreference(true);
    try {
      await updatePreferenceMutationApi({
        variables: {
          request: {
            managementFeePayment: capitalizeFirstLetter(feeType),
          },
        },
      });

      await new Promise((res) => {
        setTimeout(async () => {
          await refetchUserPreferences();
          res(true);
        }, 1500);
      });

      setToggleMitConfig((prev) => ({
        ...prev,
        isOpen: false,
        selectedId: ToggleMitModelEnum.NONE,
      }));
      setIsLoadingUpdatePreference(false);
    } catch (err) {
      setIsLoadingUpdatePreference(false);
      setToggleMitConfig((prev) => ({
        ...prev,
        isOpen: true,
        selectedId: ToggleMitModelEnum.ERROR,
      }));
    }
  };

  const mitModelConfig = [
    {
      id: ToggleMitModelEnum.ENABLE,
      // titleSection: <h1 className="text-center px-2 mb-2">{t('common:enableMit')}</h1>,
      textSection: (
        <div className="flex flex-col items-center ">
          <div className="flex flex-col gap-4 text-sm w-full max-w-[400px]">
            <p>{t('common:enableMitText1')}</p>
            <p>{t('common:enableMitText2')}</p>
          </div>
        </div>
      ),
      buttonSection: (
        <div className="flex flex-col items-center ">
          <div className="flex flex-row gap-2 items-center justify-center w-full max-w-[380px]">
            <Button
              isProcessing={isLoadingUpdatePreference}
              isDisable={isLoadingUpdatePreference}
              onClick={() => {
                handleUpdateManagentFeePayment();
              }}
              type="button"
              className="btn-accent"
            >
              {t('common:comfirm')}
            </Button>
            <Button
              isDisable={isLoadingUpdatePreference}
              onClick={() => {
                setToggleMitConfig((prev) => ({
                  ...prev,
                  selectedId: ToggleMitModelEnum.NONE,
                  isOpen: false,
                }));
              }}
              type="button"
              className="btn-accent bg-white border border-gray-500"
            >
              {t('common:autoTradeSettings.cancel')}
            </Button>
          </div>
        </div>
      ),
    },

    {
      id: ToggleMitModelEnum.DISABLE,
      //  titleSection: <h1 className="text-center px-2 mb-2">{t('common:disableMit')}</h1>,
      textSection: (
        <div className="flex flex-col items-center ">
          <div className="flex flex-col gap-4 text-sm w-full max-w-[400px]">
            <p>{t('common:disableCardMit')}</p>
          </div>
        </div>
      ),
      buttonSection: (
        <div className="flex flex-col items-center ">
          <div className="flex flex-row gap-2 items-center justify-center w-full max-w-[380px]">
            <Button
              onClick={() => {
                navigate(NavigationPath.ACCOUNTS, {
                  state: { accountViewType: AccountViewType.CONTACT_US, subject: SubjectOptionKeys.SUPPORT },
                });
              }}
              type="button"
              className="btn-accent"
            >
              {t('common:contactSupport')}
            </Button>
            <Button
              onClick={() => {
                setToggleMitConfig((prev) => ({
                  ...prev,
                  selectedId: ToggleMitModelEnum.NONE,
                  isOpen: false,
                }));
              }}
              type="button"
              className="btn-accent bg-white border border-gray-500"
            >
              {t('common:autoTradeSettings.cancel')}
            </Button>
          </div>
        </div>
      ),
    },

    {
      id: ToggleMitModelEnum.ERROR,
      titleSection: <h1 className="text-center px-2 mb-2">{t('common:autoTradeSettings.error')}</h1>,
      textSection: (
        <div className="flex flex-col items-center ">
          <div className="flex flex-col gap-4 text-sm w-full max-w-[400px]">
            <p className="text-center">{t('common:autoTradeSettings.errorMessage')}</p>
          </div>
        </div>
      ),
      buttonSection: (
        <div className="flex flex-col items-center ">
          <div className="flex flex-row gap-2 items-center justify-center w-full max-w-[200px]">
            <Button
              onClick={() => {
                setToggleMitConfig((prev) => ({
                  ...prev,
                  selectedId: ToggleMitModelEnum.NONE,
                  isOpen: false,
                }));
              }}
              type="button"
              className="btn-accent"
            >
              {t('common:autoTradeSettings.close')}
            </Button>
          </div>
        </div>
      ),
    },
  ];

  function handleEditCardClicked(card: CardDetailResponse): void {
    openSlideout(SlideOutPanelViews.CUSTOM, {
      title: t('account:payments.manageCard.title'),
      customTemplate: () => <EditCheckoutDotComCard card={card} onClose={onClose} />,
    });
  }
  return (
    <>
      <div className="flex flex-col bg-gray-50 dark:bg-slate-900 min-h-full">
        <div className="flex flex-col py-6 px-8 sm:px-10 lg:px-12 w-full gap-5">
          {!loading && !error && _cards.filter((card) => !cardHasExpired(card)).length === 0 && (
            <Alert
              className="bg-amber-100 dark:bg-amber-900/30 w-full text-sm text-amber-800 dark:text-amber-200 rounded-xl border border-amber-200 dark:border-amber-800"
              show={_cards.filter((card) => !cardHasExpired(card)).length === 0}
              icon={<WarningIcon className="min-w-[24px]" fill="currentColor" />}
            >
              {t('account:payments.addCard.cardsExpired')}
            </Alert>
          )}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('account:payments.savedCards')}</h2>
          <div className="flex flex-wrap gap-4">
            {loading ? (
              <div className="w-full py-4">
                <PaymentCardListSkeleton count={2} />
              </div>
            ) : error ? (
              <p className="text-gray-600 dark:text-gray-400">{t('common:errorResult.title')}</p>
            ) : _cards === null || _cards === undefined || _cards.length === 0 ? null : (
              _cards
                .slice()
                .sort((a) => (a.defaultMitFlag ? -1 : 1))
                .map((card, index) =>
                  ACCEPTED_PAYMENT_METHODS.find(
                    (x) => x.toLocaleLowerCase() === (card?.scheme || '').toLocaleLowerCase(),
                  ) ? (
                    <Card
                      card={card}
                      onEditCardClicked={handleEditCardClicked}
                      key={`card-${index}`}
                      expired={cardHasExpired(card)}
                    />
                  ) : null,
                )
            )}
            {!loading && (
              <AddCardButton
                onClick={() =>
                  openSlideout(SlideOutPanelViews.CUSTOM, {
                    customTemplate: () => <CardDetailsForm amount={0} flow={FlowType.ADD_CARD} />,
                    title: t('account:payments.addCard.title'),
                  })
                }
              />
            )}
          </div>
        </div>
        {!loading && !loadingUserPreferences && !toggleMitConfig.hidden && (
          <div className="flex flex-row items-center mx-6 p-4 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
            <p className="text-sm text-gray-700 dark:text-gray-300 flex-1">{t('common:enableCardMit')}</p>
            <CheckBox
              isDisabled={mitCheckBoxDisabled}
              inputClassName="max-h-[18px] max-w-[18px] min-h-[18px] min-w-[18px]"
              isChecked={toggleMitConfig.mitEnabled}
              id="toggleMitId"
              onChange={() => {
                if (toggleMitConfig.mitEnabled) {
                  setToggleMitConfig((prev) => ({
                    ...prev,
                    selectedId: ToggleMitModelEnum.DISABLE,
                    isOpen: true,
                  }));
                  return;
                }

                setToggleMitConfig((prev) => ({
                  ...prev,
                  selectedId: ToggleMitModelEnum.ENABLE,
                  isOpen: true,
                }));
              }}
            />
          </div>
        )}
      </div>
      <CardMitModel
        isDisabled={isLoadingUpdatePreference}
        config={mitModelConfig.find((x) => x.id === toggleMitConfig.selectedId) ?? {}}
        open={toggleMitConfig.isOpen}
        onClose={() => {
          setToggleMitConfig((prev) => ({
            ...prev,
            selectedId: ToggleMitModelEnum.NONE,
            isOpen: false,
          }));
        }}
      />
    </>
  );
}
